// ============================================================================
// APPROVALS API: everything addressed to the signed-in user that waits on a
// yes or no, across data requests, team invites and program-editor invites.
// ============================================================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { API_BASE, apiClient } from './client'
import { SUBSCRIPTION_KEY } from './settings'

export type ApprovalKind = 'data_consent' | 'group_monitor' | 'plan_editor'

export interface ApprovalAthlete {
  id: number
  name: string
}

/** One row of `GET /api/v1/approvals/` (backend `apis/approvals/views.py`). */
export interface Approval {
  kind: ApprovalKind
  uuid: string
  /** Server-written English, e.g. "Invited you to coach Squad". */
  title: string
  requested_by: string
  created_at: string | null
  /** Server-root paths (`/api/v1/...`). Post to them exactly as given. */
  accept_path: string
  decline_path: string
  /** group_monitor only: athletes accepting will actually ask, i.e. those
   *  who do not already share with the invitee. Absent on older backends. */
  athlete_count?: number
  athletes?: ApprovalAthlete[]
  /** group_monitor only, when the backend sends it. */
  group_name?: string
  /** plan_editor only, when the backend sends it. */
  plan_id?: number
  plan_title?: string
}

export interface ApprovalsResponse {
  approvals: Approval[]
  count: number
  /** One source failed on the server, so the list may be short. */
  partial: boolean
  unavailable_kinds: ApprovalKind[]
}

/** What an accept endpoint can hand back that the confirmation reads. Each
 *  kind returns its own serializer; these are the fields worth reading. */
export interface ApprovalAnswer {
  group_name?: string
  athletes_asked?: number
  consent_requests_created?: number
  plan_id?: number
  plan_title?: string
}

export const APPROVALS_KEY = ['approvals']

export function useApprovals(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: APPROVALS_KEY,
    queryFn: async () => {
      const { data } = await apiClient.get<ApprovalsResponse>('/approvals/')
      return data
    },
    // Sharing one cache key: every caller asks the same endpoint, so a
    // caller that only needs it sometimes does not multiply requests.
    enabled: options?.enabled ?? true,
  })
}

/**
 * Accept or decline any approval by posting the path the row carries.
 *
 * The paths are server-root (`/api/v1/...`) while the client's base already
 * ends in `/api/v1`, so the base is narrowed to the host for this call rather
 * than the path being rewritten: the server owns where acceptance lives, and
 * each kind's side effects (a team accept asks every athlete) stay on the
 * server.
 *
 * It invalidates every list any kind can change. Cheaper to reason about than
 * a per-kind table, and these are small queries. Only on SUCCESS: a failed
 * answer must leave the card where it is, with its error, and a refetch could
 * pull it out from under the person reading that error.
 */
export function useAnswerApproval() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (path: string) => {
      const { data } = await apiClient.post<ApprovalAnswer>(path, undefined, { baseURL: API_BASE })
      return data ?? {}
    },
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: APPROVALS_KEY }),
        // The Inbox carries the same invites and the navbar count, so an answer
        // there must refresh them too. Literal key: importing the inbox module
        // here would close a runtime cycle with its `import type` of Approval.
        queryClient.invalidateQueries({ queryKey: ['inbox'] }),
        queryClient.invalidateQueries({ queryKey: ['consents', 'my'] }),
        queryClient.invalidateQueries({ queryKey: ['athlete-context'] }),
        queryClient.invalidateQueries({ queryKey: ['groups', 'my'] }),
        queryClient.invalidateQueries({ queryKey: ['activity-plans'] }),
        // A focused invite's own detail read, so an answered invite stops
        // reading as pending once the confirmation is dismissed.
        queryClient.invalidateQueries({ queryKey: ['consent', 'invite'] }),
        // Joining a team is what funds support staff, so their paywall
        // decision flips on an accept.
        queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_KEY }),
      ]),
  })
}
